/* eslint-disable no-nested-ternary */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { map, reduce } from 'lodash';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Formik, Form } from 'formik';
import { Button, FormAlerts, Field, CardTitle, PseudoStates, useFeatures } from '@cordata/buffet';
import { format } from 'date-fns';

import SliceFields from '~/components/Slice/Fields';
import useReferenceData from '~/utils/reference-data/hook';
import useApi from '~/utils/api/hook';

const VKField = ({
  v, k,
}) => {
  const {
    meta: {
      nameAttr,
    },
    config: {
      actions: {
        parseFieldType,
      },
    },
  } = useReferenceData();
  const { isSuperUser } = useFeatures();

  return (
    <Field
      name={k}
      type={parseFieldType(v, k).type}
      required={v.required && v.type !== 'BOOLEAN'}
      options={v.options}
      getOptionValue={v.getOptionValue}
      getOptionLabel={v.getOptionLabel}
      disabled={v.disabled || (k === nameAttr && !isSuperUser)}
      readOnly={v.readOnly}
      creatable={v.creatable}
    />
  );
};

VKField.propTypes = {
  k: PropTypes.string.isRequired,
  v: PropTypes.shape().isRequired,
};

const ReferenceDataForm = ({
  status,
  initialValues,
  handleCancel,
  handleSubmit,
}) => {
  const {
    type,
    meta: {
      fields,
      nameAttr,
      labelAttr,
      typeAttr,
      validationSchema,
    },
    config: { customFields = {} },
  } = useReferenceData();
  const { api } = useApi();
  const [nonStandardFields, setNonStandardFields] = useState({});
  const [modifiedUser, setModifiedUser] = useState('unknown');

  const standardFields = [nameAttr, labelAttr, typeAttr];

  useEffect(() => {
    if (fields) {
      const f = reduce(Object.keys(fields), (a, m) => {
        if (
          ![
            ...standardFields,
            ...Object.keys(customFields),
            'language',
            'dialect',
            'market',
            'specialty',
            'subSpecialty',
          ].includes(m) && !fields[m].primaryKey
        ) {
          return {
            ...a,
            [m]: fields[m],
          };
        }

        return a;
      }, {});

      setNonStandardFields(f);
    }
  }, [fields]);

  useEffect(() => {
    setModifiedUser('unknown');
    if (initialValues.updatedUid || initialValues.createdUid) {
      const u = initialValues.updatedUid || initialValues.createdUid;

      api
        .get(`app-user/${u}`)
        .then(r => r.data)
        .then((r) => {
          if (r) {
            setModifiedUser(r.data.attributes.userName);
          }
        });
    }
  }, [initialValues.updatedUid, initialValues.createdUid]);

  return (
    <Modal isOpen={!!initialValues} backdrop="static" size="xl">
      <Formik
        name="ReferenceDataForm"
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          isSubmitting, submitCount, errors, values,
        }) => (
          <Form noValidate>
            <ModalHeader>{!values.id ? 'Add' : 'Edit'} {type}</ModalHeader>

            <ModalBody className="p-0">
              {status === 'success' ? (
                <>
                  <fieldset>
                    { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

                    {fields && map(fields, (v, k) =>
                      standardFields.includes(k) &&
                      !v.primaryKey && (
                        <VKField
                          key={k}
                          v={v}
                          k={k}
                        />
                    ))}
                  </fieldset>

                  {!!Object.keys(nonStandardFields).length && (
                    <fieldset>
                      {map(nonStandardFields, (v, k) => (
                        <VKField
                          key={k}
                          v={v}
                          k={k}
                        />
                      ))}
                    </fieldset>
                  )}

                  {!!Object.keys(customFields).length && (
                    <fieldset>
                      {map(customFields, (v, k) => (
                        <v.component
                          key={k}
                        />
                      ))}
                    </fieldset>
                  )}

                  <fieldset>
                    <Field
                      name="isActive"
                      type="checkbox"
                    />
                  </fieldset>

                  <fieldset>
                    <CardTitle>Context Selector</CardTitle>

                    <SliceFields />

                  </fieldset>

                  {values.id && (
                    <fieldset>
                      <Field
                        name="id"
                        disabled
                      />
                    </fieldset>
                  )}
                </>
              )
                : <PseudoStates status={status} />}
            </ModalBody>

            <ModalFooter className="d-flex">
              {(initialValues.updatedDate || initialValues.createdDate) && (
                <div className="mr-auto text-muted text-small">
                  Last Updated: {format(initialValues.updatedDate || initialValues.createdDate, 'ddd MMM Do YYYY [@] h:mm A')} by {modifiedUser}
                </div>
              )}

              <div className="ml-auto">
                <Button
                  outline
                  type="cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </div>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

ReferenceDataForm.propTypes = {
  initialValues: PropTypes.shape(),
  handleCancel: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

ReferenceDataForm.defaultProps = {
  initialValues: {},
};

export default ReferenceDataForm;
